/**
 * Professional Resilient Scroll Restoration Engine for Vue 3 Single Page Application
 * Handles:
 * - Manual browser history.scrollRestoration control to prevent native scroll-anchoring jumps to Footer
 * - Waiting for Vue Router <router-view> DOM swap (nextTick) before measuring/scrolling
 * - Waiting for dynamic content (API data, images, async components) inside <main> to expand DOM height
 * - Pinpoint restoration of the exact clicked product card viewport offset
 * - Prevention of saved position corruption during popstate and programmatic scroll events
 * - Temporary disabling of CSS overflow-anchor during restoration & stabilization window
 * - Real user gesture detection (wheel, touchstart, keydown, pointerdown) to cleanly abort auto-restoration
 */

import { nextTick } from 'vue'

const SCROLL_STORAGE_PREFIX = 'ms_scroll_pos_'
const CLICKED_CONTEXT_KEY = 'ms_last_clicked_context'
const MAX_RESTORE_TIME = 3000 // 3 seconds max retry window for slow networks
const STABILIZE_WINDOW_MS = 450 // Hold lock after reaching target to absorb late image/layout shifts

let currentObserver = null
let currentRaf = null
let isUserInteracting = false
let currentCancelListeners = []
let activeIntervalTimer = null
let stabilizeTimer = null
let isRestoringScroll = false

// Track active route identity so scroll listener never saves under the wrong route during popstate
let activeRouteFullPath = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/'
let activeHistoryPos = typeof window !== 'undefined' ? (window.history.state?.position ?? null) : null
let lastKnownScroll = { x: 0, y: 0 }

// Track whether navigation is POP (Back/Forward)
let isPopNavigation = false
let lastPopTimestamp = 0

if (typeof window !== 'undefined') {
  // Disable browser's native scroll restoration so it never fights Vue Router or anchors to the Footer
  if ('scrollRestoration' in window.history) {
    try {
      window.history.scrollRestoration = 'manual'
    } catch (e) {}
  }

  window.addEventListener('popstate', () => {
    isPopNavigation = true
    isRestoringScroll = true
    lastPopTimestamp = Date.now()
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.style.overflowAnchor = 'none'
    }
  })
}

export function isPopstateNavigation() {
  return isPopNavigation || (Date.now() - lastPopTimestamp < 800)
}

export function resetPopNavigationFlag() {
  isPopNavigation = false
  lastPopTimestamp = 0
}

export function isRestoring() {
  return isRestoringScroll
}

export function setActiveRouteForScroll(route) {
  if (typeof window === 'undefined' || !route) return
  activeRouteFullPath = route.fullPath || (window.location.pathname + window.location.search)
  activeHistoryPos = typeof window.history.state?.position === 'number' ? window.history.state.position : null
  if (!isRestoringScroll) {
    lastKnownScroll = {
      x: window.scrollX || window.pageXOffset || 0,
      y: window.scrollY || window.pageYOffset || 0
    }
  }
}

export function getHistoryKey(route, explicitStatePos = undefined) {
  if (typeof window === 'undefined') return ''
  const statePos = explicitStatePos !== undefined
    ? explicitStatePos
    : (typeof window.history.state?.position === 'number' ? window.history.state.position : activeHistoryPos)
  const path = route ? route.fullPath : activeRouteFullPath
  if (typeof statePos === 'number') {
    return `${statePos}_${path}`
  }
  return path
}

/**
 * Called directly from ProductCard / product link @click BEFORE route navigation begins.
 * Captures the exact scrollY and the clicked card's viewport top offset.
 */
export function saveClickedProductContext(productKey, cardEl = null) {
  if (typeof window === 'undefined') return
  const currentX = window.scrollX || window.pageXOffset || 0
  const currentY = window.scrollY || window.pageYOffset || 0
  const path = activeRouteFullPath || (window.location.pathname + window.location.search)
  const statePos = typeof window.history.state?.position === 'number' ? window.history.state.position : activeHistoryPos

  const pos = { x: currentX, y: currentY }
  lastKnownScroll = pos

  try {
    if (typeof statePos === 'number') {
      sessionStorage.setItem(SCROLL_STORAGE_PREFIX + `${statePos}_${path}`, JSON.stringify(pos))
    }
    sessionStorage.setItem(SCROLL_STORAGE_PREFIX + path, JSON.stringify(pos))

    let viewportTop = null
    if (cardEl && typeof cardEl.getBoundingClientRect === 'function') {
      const rect = cardEl.getBoundingClientRect()
      viewportTop = Math.round(rect.top)
    }

    if (productKey) {
      sessionStorage.setItem('ms_last_clicked_product', String(productKey))
      sessionStorage.setItem(CLICKED_CONTEXT_KEY, JSON.stringify({
        path,
        productKey: String(productKey),
        scrollY: currentY,
        viewportTop,
        timestamp: Date.now()
      }))
    }
  } catch (e) {}
}

// Track real-time scroll position of current page
let pendingScrollSaveRaf = null
export const updateCurrentPosition = () => {
  if (typeof window === 'undefined' || isRestoringScroll || isPopNavigation) return
  const path = activeRouteFullPath
  if (!path || path.startsWith('/admin')) return

  const pos = {
    x: window.scrollX || window.pageXOffset || 0,
    y: window.scrollY || window.pageYOffset || 0
  }
  lastKnownScroll = pos

  try {
    if (typeof activeHistoryPos === 'number') {
      sessionStorage.setItem(SCROLL_STORAGE_PREFIX + `${activeHistoryPos}_${path}`, JSON.stringify(pos))
    }
    sessionStorage.setItem(SCROLL_STORAGE_PREFIX + path, JSON.stringify(pos))
  } catch (e) {}
}

export function saveScrollPosition(route) {
  if (typeof window === 'undefined' || isRestoringScroll || isPopNavigation) return
  const path = route ? route.fullPath : activeRouteFullPath
  if (!path || path.startsWith('/admin')) return

  // Use lastKnownScroll from passive scroll listener when available to avoid forced layout / synchronous reflow
  const currentY = (typeof lastKnownScroll.y === 'number' && lastKnownScroll.y > 0)
    ? lastKnownScroll.y
    : (window.scrollY || window.pageYOffset || 0)
  const currentX = (typeof lastKnownScroll.x === 'number' && lastKnownScroll.x > 0)
    ? lastKnownScroll.x
    : (window.scrollX || window.pageXOffset || 0)
  const pos = {
    x: currentX || 0,
    y: currentY || 0
  }

  try {
    if (typeof activeHistoryPos === 'number') {
      sessionStorage.setItem(SCROLL_STORAGE_PREFIX + `${activeHistoryPos}_${path}`, JSON.stringify(pos))
    }
    sessionStorage.setItem(SCROLL_STORAGE_PREFIX + path, JSON.stringify(pos))
  } catch (e) {}
}

export function getSavedScrollPosition(route, savedPosition) {
  if (typeof window === 'undefined') return null
  const statePos = window.history.state?.position
  const path = route ? route.fullPath : (window.location.pathname + window.location.search)

  try {
    // 1. Prefer exact history state position + path in sessionStorage
    if (typeof statePos === 'number') {
      const item = sessionStorage.getItem(SCROLL_STORAGE_PREFIX + `${statePos}_${path}`)
      if (item) {
        const parsed = JSON.parse(item)
        if (parsed && typeof parsed.y === 'number') return parsed
      }
    }
    // 2. Fallback to path in sessionStorage
    const fallbackItem = sessionStorage.getItem(SCROLL_STORAGE_PREFIX + path)
    if (fallbackItem) {
      const parsed = JSON.parse(fallbackItem)
      if (parsed && typeof parsed.y === 'number') return parsed
    }
  } catch (e) {}

  // 3. Fallback to Vue Router's savedPosition
  if (savedPosition && typeof savedPosition.top === 'number') {
    return { x: savedPosition.left || 0, y: savedPosition.top }
  }
  return null
}

export function stopScrollRestoration() {
  isRestoringScroll = false
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.style.overflowAnchor = ''
  }
  if (currentObserver) {
    currentObserver.disconnect()
    currentObserver = null
  }
  if (currentRaf) {
    cancelAnimationFrame(currentRaf)
    currentRaf = null
  }
  if (activeIntervalTimer) {
    clearInterval(activeIntervalTimer)
    activeIntervalTimer = null
  }
  if (stabilizeTimer) {
    clearTimeout(stabilizeTimer)
    stabilizeTimer = null
  }
  cleanupUserListeners()
}

function cleanupUserListeners() {
  if (currentCancelListeners.length > 0) {
    currentCancelListeners.forEach(({ type, fn }) => {
      window.removeEventListener(type, fn, { passive: true })
    })
    currentCancelListeners = []
  }
}

/**
 * Helper to resolve the exact scrollY for a previously clicked product card on this route.
 * Picks the card instance closest to targetY so duplicate products on the same page never jump to the wrong section.
 */
function resolveClickedCardTargetY(targetY, routePath) {
  try {
    const rawCtx = sessionStorage.getItem(CLICKED_CONTEXT_KEY)
    const fallbackKey = sessionStorage.getItem('ms_last_clicked_product')
    let ctx = null
    if (rawCtx) {
      ctx = JSON.parse(rawCtx)
    }

    const productKey = (ctx && ctx.path === routePath) ? ctx.productKey : fallbackKey
    if (!productKey) return targetY

    const mainEl = document.querySelector('main') || document
    const selector = `[data-product-slug="${CSS.escape(productKey)}"], [data-product-id="${CSS.escape(productKey)}"]`
    const candidates = Array.from(mainEl.querySelectorAll(selector))
    if (candidates.length === 0) return targetY

    const currentScrollY = window.scrollY || window.pageYOffset || 0
    const desiredViewportTop = (ctx && ctx.path === routePath && typeof ctx.viewportTop === 'number')
      ? ctx.viewportTop
      : Math.round((window.innerHeight || 800) * 0.22)

    let bestY = targetY
    let minDiff = Infinity

    for (const el of candidates) {
      const rect = el.getBoundingClientRect()
      if (rect.height === 0 && rect.width === 0) continue
      const absTop = currentScrollY + rect.top
      const candidateScrollY = Math.max(0, Math.round(absTop - desiredViewportTop))
      const diff = Math.abs(candidateScrollY - targetY)
      if (diff < minDiff) {
        minDiff = diff
        bestY = candidateScrollY
      }
    }

    // Only adjust if the matched card is within a reasonable distance of the saved scrollY
    if (minDiff <= 900) {
      return bestY
    }
  } catch (e) {}
  return targetY
}

/**
 * Resiliently restores scroll position to target (x, y)
 * Waits for Vue Router's <router-view> DOM swap (nextTick) and <main> content readiness.
 */
export function restoreScrollPosition(target, route = null) {
  if (typeof window === 'undefined') return
  stopScrollRestoration()

  const targetX = target?.x ?? target?.left ?? 0
  const baseTargetY = target?.y ?? target?.top ?? 0
  const routePath = route ? route.fullPath : (window.location.pathname + window.location.search)

  // If target is top 0, execute immediately and return
  if (baseTargetY <= 0 && targetX <= 0) {
    window.scrollTo({ left: 0, top: 0, behavior: 'instant' })
    if (document.documentElement) document.documentElement.scrollTop = 0
    if (document.body) document.body.scrollTop = 0
    return
  }

  isRestoringScroll = true
  isUserInteracting = false

  // Disable CSS scroll anchoring so the browser never anchors to <MainFooter> while <main> expands above it
  if (document.documentElement) {
    document.documentElement.style.overflowAnchor = 'none'
  }

  // Listen for real user interaction to cancel auto-restoration immediately
  const cancelEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown']
  const onUserCancel = () => {
    isUserInteracting = true
    stopScrollRestoration()
    updateCurrentPosition()
  }
  cancelEvents.forEach(type => {
    window.addEventListener(type, onUserCancel, { passive: true, once: true })
    currentCancelListeners.push({ type, fn: onUserCancel })
  })

  const startTime = performance.now()
  let reachedTargetTimestamp = 0
  let cachedEffectiveTargetY = null

  const tryScroll = () => {
    if (isUserInteracting) {
      stopScrollRestoration()
      return true
    }

    // Check if the route component inside <main> is still loading async data
    const isPageLoading = !!document.querySelector('[data-page-loading="true"]')

    // Resolve exact targetY (accounting for clicked product card position if available)
    let effectiveTargetY = baseTargetY
    if (!isPageLoading) {
      if (cachedEffectiveTargetY !== null) {
        effectiveTargetY = cachedEffectiveTargetY
      } else {
        const resolved = resolveClickedCardTargetY(baseTargetY, routePath)
        if (resolved !== baseTargetY) {
          cachedEffectiveTargetY = resolved
          effectiveTargetY = resolved
        }
      }
    }

    // Batch all layout reads before performing any write
    const docHeight = Math.max(
      document.documentElement?.scrollHeight || 0,
      document.body?.scrollHeight || 0
    )
    const clientHeight = window.innerHeight || document.documentElement?.clientHeight || 0
    const maxScrollY = Math.max(0, docHeight - clientHeight)

    // Inspect <main> height to prevent jumping past unloaded content
    const mainEl = document.querySelector('main')
    const mainBottom = mainEl ? (mainEl.offsetTop + mainEl.offsetHeight) : docHeight
    const isMainTooShort = mainEl ? (effectiveTargetY > mainBottom - 100 && mainEl.offsetHeight < 1400) : false

    // Scroll toward effectiveTargetY (if <main> is still loading/short, clamp inside <main>)
    const safeScrollY = (isPageLoading || isMainTooShort)
      ? Math.min(effectiveTargetY, Math.max(0, mainBottom - clientHeight))
      : effectiveTargetY

    // Write scroll
    window.scrollTo({ left: targetX, top: safeScrollY, behavior: 'instant' })
    lastKnownScroll = { x: targetX, y: safeScrollY }

    // Avoid reading window.scrollY immediately after window.scrollTo to prevent forced synchronous reflow / layout thrashing
    const projectedY = Math.min(safeScrollY, maxScrollY)
    const isClampedAtBottom = effectiveTargetY > (maxScrollY + 15)
    const isAtTarget = !isPageLoading && !isMainTooShort && !isClampedAtBottom && Math.abs(projectedY - effectiveTargetY) <= 10

    if (isAtTarget) {
      if (!reachedTargetTimestamp) {
        reachedTargetTimestamp = performance.now()
      }

      // Hold the scroll lock through STABILIZE_WINDOW_MS so late images / layout shifts cannot push the page down
      if (performance.now() - reachedTargetTimestamp >= STABILIZE_WINDOW_MS) {
        try {
          sessionStorage.removeItem('ms_last_clicked_product')
          sessionStorage.removeItem(CLICKED_CONTEXT_KEY)
        } catch (e) {}
        lastKnownScroll = { x: targetX, y: effectiveTargetY }
        stopScrollRestoration()
        return true
      }
      return false
    } else {
      // Reset stabilization timer if DOM height shifted and moved us away
      reachedTargetTimestamp = 0
    }

    // Check max timeout
    if (performance.now() - startTime > MAX_RESTORE_TIME) {
      try {
        sessionStorage.removeItem('ms_last_clicked_product')
        sessionStorage.removeItem(CLICKED_CONTEXT_KEY)
      } catch (e) {}
      stopScrollRestoration()
      return true
    }

    return false
  }

  // Attempt 1: Immediate execution
  if (tryScroll()) return

  // CRITICAL: Wait for Vue's nextTick so <router-view> has unmounted previous page
  // and mounted target page before measuring or scrolling
  nextTick(() => {
    if (!isRestoringScroll || isUserInteracting) return

    if (tryScroll()) return

    // Setup ResizeObserver to trigger scroll adjustment on actual DOM mutations
    if (typeof ResizeObserver !== 'undefined') {
      currentObserver = new ResizeObserver(() => {
        if (isUserInteracting || !isRestoringScroll) {
          stopScrollRestoration()
          return
        }
        if (currentRaf) cancelAnimationFrame(currentRaf)
        currentRaf = requestAnimationFrame(() => {
          if (!isRestoringScroll || isUserInteracting) return
          if (tryScroll()) {
            stopScrollRestoration()
          }
        })
      })

      if (document.documentElement) currentObserver.observe(document.documentElement)
      if (document.body) currentObserver.observe(document.body)
      const mainEl = document.querySelector('main')
      if (mainEl) currentObserver.observe(mainEl)
    }

    // Throttled heartbeat polling (100ms instead of 40ms) to ensure completion even if ResizeObserver misses late async loads
    activeIntervalTimer = setInterval(() => {
      if (isUserInteracting || !isRestoringScroll || performance.now() - startTime > MAX_RESTORE_TIME) {
        stopScrollRestoration()
        return
      }
      if (tryScroll()) {
        stopScrollRestoration()
      }
    }, 100)
  })
}

// Global scroll event listener to keep current page scroll position updated
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => {
    if (isRestoringScroll || isPopNavigation) return
    if (pendingScrollSaveRaf) return
    pendingScrollSaveRaf = requestAnimationFrame(() => {
      pendingScrollSaveRaf = null
      updateCurrentPosition()
    })
  }, { passive: true })
}
